import { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Briefcase, Lock, Lightbulb } from "lucide-react";
import api from "@/lib/api";

/**
 * WorkPatternsCard — Laura Piccinini's curated 7 work patterns (May 2026).
 *
 * Backend: GET /api/voice-mood/work-patterns
 *   - Returns up to 2 patterns that exceed thresholds (N≥4, ≥20%)
 *   - Each pattern includes interpretation + ONE weekly-rotated advice
 *
 * Frontend never shows the 4 raw variables to beta testers — only the
 * pattern name, the narrative interpretation, and a single rotating advice.
 * The clinician sees, in addition, the shorthand (e.g. "C↑ Ct↓ E↑ P↓") and
 * the N count.
 */
const SkeletonCard = ({ title }) => (
  <Card className="border" data-testid="work-patterns-loading">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm flex items-center gap-2">
        <Briefcase className="w-4 h-4 text-slate-500" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="space-y-2">
        <div className="h-3 bg-slate-100 rounded animate-pulse w-2/3" />
        <div className="h-3 bg-slate-100 rounded animate-pulse w-full" />
        <div className="h-3 bg-slate-100 rounded animate-pulse w-5/6" />
      </div>
    </CardContent>
  </Card>
);

// Cautious-language prefix per maturity tier (the same interpretation text is
// prepended with a tier-dependent qualifier).
const MATURITY_LEAD_IN = {
  emerging:  "Sta emergendo un pattern, ancora da confermare nel tempo.",
  recurring: "Si conferma un pattern ricorrente.",
  stable:    "Si conferma in modo stabile un pattern.",
};

export const WorkPatternsCard = ({ isClinician = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/voice-mood/work-patterns")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SkeletonCard title={isClinician ? "Pattern lavorativi" : "Come vivi il lavoro"} />;
  if (!data) return null;

  const title = isClinician ? "Pattern lavorativi" : "Come vivi il lavoro";
  const subtitle = isClinician
    ? "Configurazioni curate Piccinini 2026 sull'attività lavoro (ultimi 30 giorni)"
    : "Come si stanno organizzando i tuoi momenti di lavoro nelle ultime settimane.";

  return (
    <Card className="border" data-testid="work-patterns-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-slate-600" />
          {title}
        </CardTitle>
        <p className="text-[10px] text-muted-foreground">{subtitle}</p>
      </CardHeader>

      <CardContent>
        {!data.unlocked ? (
          <div className="flex items-start gap-2 text-xs text-muted-foreground" data-testid="work-patterns-locked">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Servono almeno {data.min_n_threshold} check-in di lavoro negli ultimi 30 giorni
              per leggere i pattern. Attualmente: {data.n_work_total}.
            </span>
          </div>
        ) : data.patterns.length === 0 ? (
          <p className="text-xs text-muted-foreground italic" data-testid="work-patterns-empty">
            I tuoi momenti di lavoro nelle ultime settimane non disegnano ancora un pattern
            netto. Continuando con i check-in, i pattern emergeranno con più chiarezza.
          </p>
        ) : (
          <div className="space-y-5" data-testid="work-patterns-list">
            {data.patterns.map((p) => (
              <article
                key={p.id}
                className="border rounded-md p-3 space-y-3"
                data-testid={`work-pattern-${p.id}`}
              >
                {/* Pattern name + clinician shorthand */}
                <header className="flex items-start justify-between gap-2">
                  <h4 className="text-sm font-semibold text-slate-800">{p.name}</h4>
                  {isClinician && (
                    <span className="text-[10px] text-muted-foreground tracking-wide">
                      {p.shorthand} · N={p.n} · {p.pct}%
                    </span>
                  )}
                </header>

                {/* Maturity lead-in (cautious wording) + interpretation */}
                <p className="text-xs leading-relaxed text-foreground/90">
                  <span className="italic text-muted-foreground">{MATURITY_LEAD_IN[p.maturity] || ""}</span>
                  {" "}
                  {p.interpretation}
                </p>

                {/* 2026-06 (Opzione A): firma sociale del pattern.
                    Mostra in che contesto sociale questo pattern si verifica
                    più spesso, senza moltiplicare le categorie. */}
                {Array.isArray(p.social_signature) && p.social_signature.length > 0 && (
                  <div
                    className="bg-slate-50 border border-slate-200 rounded-md p-2.5"
                    data-testid={`work-pattern-${p.id}-social-signature`}
                  >
                    <p className="text-[11px] font-medium text-slate-700 mb-1">
                      Si manifesta soprattutto:
                    </p>
                    <ul className="space-y-0.5">
                      {p.social_signature.map((s, i) => (
                        <li key={i} className="text-xs text-foreground/85 flex items-baseline gap-1.5">
                          <span className="text-slate-400">·</span>
                          <span>
                            <strong>{s.context_label}</strong> ({s.pct}%
                            {s.dominant_quality_label && s.dominant_quality_label !== "neutra"
                              ? ` · qualità ${s.dominant_quality_label}`
                              : ""})
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Weekly advice — one only, rotated by ISO week */}
                {p.advice && (
                  <div
                    className="bg-amber-50/60 border border-amber-200/60 rounded-md p-2.5 space-y-1"
                    data-testid={`work-pattern-${p.id}-advice`}
                  >
                    <div className="flex items-center gap-1.5 text-[11px] font-medium text-amber-900 tracking-wide">
                      <Lightbulb className="w-3 h-3" strokeWidth={2} />
                      <span>SUGGERIMENTO DELLA SETTIMANA</span>
                    </div>
                    <p className="text-xs font-semibold text-slate-800 leading-snug">
                      {p.advice.title}
                    </p>
                    <p className="text-xs text-foreground/80 leading-relaxed">
                      {p.advice.text}
                    </p>
                  </div>
                )}
              </article>
            ))}

            {/* Clinician-only diagnostics */}
            {isClinician && (
              <p className="text-[10px] text-muted-foreground italic border-t border-dashed border-slate-200 pt-2">
                [Clinico] {data.n_work_total} check-in di lavoro · {data.n_unbinned} non classificati
                {" "}· settimana ISO {data.iso_week} · soglie: N≥{data.min_pattern_count}, ≥{data.min_pattern_pct}%
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkPatternsCard;
